import type { Product } from '@clockwise/clients/products';
import { LifecycleStatus } from '@clockwise/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronsUpDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { SearchInput } from '@/components/custom/search-input';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { productsApi, serviceRolesApi } from '@/lib/api';
import { popoverScrollFix } from '@/lib/popover-scroll-fix';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { warnIfTruncated } from '@/lib/warn-if-truncated';

// Sortable columns. `serviceRole` sorts client-side within the current
// page (the products API doesn't accept role-name as a sortField); `name`
// sorts server-side.
type SortField = 'serviceRole' | 'name';
type SortDirection = 'asc' | 'desc';

// Multi-select picker: shows the products catalog and emits the selected
// products on submit. Caller decides what to do with them (e.g. append a
// SOW line per product).
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Fires with the user's selection. Emits both the Product objects AND a
   * snapshot of `serviceRoleId → shortCode` for every role this sheet
   * resolved (the external-roles list plus the by-id top-up). The second
   * arg lets the caller render role chips immediately — without waiting
   * for whatever roles query the page-level lookup runs — by reading short
   * codes that are already in this sheet's hand when the user clicks "Add".
   */
  onSubmit: (products: Product[], serviceRoleCodeByRoleId: ReadonlyMap<string, string>) => void;
  // Product ids already on the SOW — shown as "added" and not re-selectable.
  alreadyAddedIds?: Set<string>;
}

export function BulkAddProductsSheet({ open, onOpenChange, onSubmit, alreadyAddedIds }: Props) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<Record<string, Product>>({});
  // Multi-select role filter — empty array = no role filter applied. We
  // use accessibleByRoleIds on the API (it's the only multi-id filter the
  // products endpoint accepts and matches "primary role is X OR X is in
  // the visibleTo list" — close enough to the user intent of "filter by
  // role").
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [roleFilterOpen, setRoleFilterOpen] = useState(false);
  const [sort, setSort] = useState<{ field: SortField; direction: SortDirection }>({
    field: 'serviceRole',
    direction: 'asc',
  });

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelected({});
      setRoleFilter([]);
      setSort({ field: 'serviceRole', direction: 'asc' });
    }
  }, [open]);

  useEffect(() => {
    const h = setTimeout(() => setDebounced(search), 200);
    return () => clearTimeout(h);
  }, [search]);

  // External (non-internal) service roles drive the filter popover and
  // the per-row Service role badge.
  const rolesQuery = useQuery({
    queryKey: queryKeys.serviceRoles.list({
      active: true,
      internal: false,
      sortField: 'name',
      sortDirection: 'asc',
      limit: 200,
    }),
    queryFn: () =>
      serviceRolesApi.list({
        active: true,
        internal: false,
        sortField: 'name',
        sortDirection: 'asc',
        limit: 200,
      }),
    enabled: open,
  });
  warnIfTruncated(rolesQuery.data, 'bulk-add-products service-role badges');
  const externalRoles = rolesQuery.data?.data ?? [];
  const externalRoleIds = useMemo(() => new Set(externalRoles.map((r) => r.id)), [externalRoles]);

  // Only `name` is a server-recognized sort field for products; the
  // serviceRole column sorts client-side within the fetched page.
  const apiSort: { sortField: 'name'; sortDirection: SortDirection } =
    sort.field === 'name'
      ? { sortField: 'name', sortDirection: sort.direction }
      : { sortField: 'name', sortDirection: 'asc' };

  const query = useQuery({
    queryKey: queryKeys.products.list({
      q: debounced || undefined,
      status: LifecycleStatus.Active,
      // Internal activities are non-billable, so they can't back a SOW
      // line.
      internal: false,
      accessibleByRoleIds: roleFilter.length > 0 ? roleFilter : undefined,
      ...apiSort,
      limit: 100,
      offset: 0,
    }),
    queryFn: () =>
      productsApi.list({
        q: debounced || undefined,
        status: LifecycleStatus.Active,
        internal: false,
        accessibleByRoleIds: roleFilter.length > 0 ? roleFilter : undefined,
        ...apiSort,
        limit: 100,
        offset: 0,
      }),
    placeholderData: keepPreviousData,
    enabled: open,
  });

  // A billable activity can reference a role the external-roles query above
  // never returns: `products.internal` and `service_roles.internal` are
  // independent columns, so a customer-facing activity can sit under a role
  // someone flagged internal — and a role can be deactivated while its
  // activities stay active. Those rows rendered a raw uuid in the Service
  // role column, so resolve the stragglers by id, the same fallback the SOW
  // detail page runs for the roles on saved lines.
  const unresolvedRoleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of query.data?.data ?? []) {
      if (!externalRoleIds.has(p.serviceRoleId)) ids.add(p.serviceRoleId);
    }
    // Sorted so the query key is stable across product-page reorders.
    return [...ids].sort();
  }, [query.data, externalRoleIds]);

  // Wait for the external list to settle before topping up: the two queries
  // fire together on open and genuinely race, and if products land first
  // `unresolvedRoleIds` is momentarily EVERY role on the page — which would
  // fetch the whole set by id, then refetch the real stragglers under a new
  // query key, blanking the names it had just resolved. `isPending` goes
  // false on error too, so a 403 on the list still falls through to
  // resolving every role by id.
  const referencedRolesQuery = useQuery({
    queryKey: queryKeys.serviceRoles.byIds(unresolvedRoleIds),
    queryFn: () => serviceRolesApi.findByIds(unresolvedRoleIds),
    enabled: open && !rolesQuery.isPending && unresolvedRoleIds.length > 0,
  });

  // The filter popover still lists external roles only — these two maps are
  // purely for labelling the rows we actually fetched. The two sources are
  // disjoint by construction (`unresolvedRoleIds` excludes external ids).
  const labelledRoles = useMemo(
    () => [...externalRoles, ...(referencedRolesQuery.data?.data ?? [])],
    [externalRoles, referencedRolesQuery.data],
  );
  const roleNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of labelledRoles) m.set(r.id, r.name);
    return m;
  }, [labelledRoles]);
  // Snapshot of role id → shortCode, emitted to the caller on submit so
  // they can render chips without waiting on a separate roles query.
  const roleShortCodeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of labelledRoles) {
      // Defensive: don't bake empty short codes into the map — the chip
      // component would falsy-check them anyway, but the caller's local
      // lookup should treat "unknown" as null rather than "".
      if (r.shortCode) m.set(r.id, r.shortCode);
    }
    return m;
  }, [labelledRoles]);

  // True while a row's role name is still in flight — either the external
  // list or the by-id top-up. Rows render a skeleton rather than flashing
  // the raw-uuid fallback for the frames between products and roles landing.
  const roleLabelsPending =
    rolesQuery.isPending || (unresolvedRoleIds.length > 0 && referencedRolesQuery.isPending);

  // Composite sort: server orders by name asc (always); when the user
  // sorts by Service role we re-sort client-side by role name. JS sort is
  // stable, so items with the same role retain their name-asc order — the
  // result is "Service role, then Name" out of the box. Inverting direction
  // is done via the comparator (not Array.reverse) so the secondary sort
  // stays stable.
  const products = useMemo(() => {
    const rows = query.data?.data ?? [];
    if (sort.field !== 'serviceRole') return rows;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const an = roleNameById.get(a.serviceRoleId) ?? '';
      const bn = roleNameById.get(b.serviceRoleId) ?? '';
      return an.localeCompare(bn) * dir;
    });
  }, [query.data, sort, roleNameById]);

  const selectedCount = Object.keys(selected).length;

  const toggle = (p: Product) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[p.id]) delete next[p.id];
      else next[p.id] = p;
      return next;
    });
  };

  const visibleSelectableIds = products.filter((p) => !alreadyAddedIds?.has(p.id)).map((p) => p.id);
  const allVisibleSelected =
    visibleSelectableIds.length > 0 && visibleSelectableIds.every((id) => selected[id] != null);

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = { ...prev };
      if (allVisibleSelected) {
        for (const id of visibleSelectableIds) delete next[id];
      } else {
        for (const p of products) {
          if (!alreadyAddedIds?.has(p.id)) next[p.id] = p;
        }
      }
      return next;
    });
  };

  const toggleRoleFilter = (roleId: string) => {
    setRoleFilter((prev) =>
      prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId],
    );
  };

  const toggleSort = (field: SortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' },
    );
  };

  const roleFilterLabel =
    roleFilter.length === 0
      ? 'All service roles'
      : roleFilter.length === 1
        ? (roleNameById.get(roleFilter[0]!) ?? '1 role')
        : `${roleFilter.length} roles`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-b">
          <SheetTitle>Add lines from activities</SheetTitle>
          <SheetDescription>
            Pick one or more activities. A line is appended per selection.
          </SheetDescription>
        </SheetHeader>

        <div className="flex items-center gap-2 border-b p-3">
          <SearchInput
            placeholder="Search codes, name, description…"
            value={search}
            onValueChange={setSearch}
            className="w-60"
          />
          <Popover open={roleFilterOpen} onOpenChange={setRoleFilterOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" className="w-52 justify-between">
                <span className="truncate">{roleFilterLabel}</span>
                <ChevronsUpDown className="ml-2 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start">
              <Command>
                <CommandInput placeholder="Search roles…" />
                {/* Explicit max-h so the role list caps at ~9 rows; popoverScrollFix
                    lets it scroll despite the wrapping Sheet's scroll-lock (the
                    popover is portaled out of the Sheet). See lib/popover-scroll-fix.ts. */}
                <CommandList className="max-h-72 overflow-y-auto" {...popoverScrollFix}>
                  <CommandEmpty>No roles match.</CommandEmpty>
                  <CommandGroup>
                    {externalRoles.map((r) => {
                      const isSelected = roleFilter.includes(r.id);
                      return (
                        <CommandItem
                          key={r.id}
                          value={r.name}
                          onSelect={() => toggleRoleFilter(r.id)}
                        >
                          <Check className={cn('mr-2', isSelected ? 'opacity-100' : 'opacity-0')} />
                          {r.name}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
                {roleFilter.length > 0 ? (
                  <div className="border-t p-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => setRoleFilter([])}
                    >
                      Clear ({roleFilter.length})
                    </Button>
                  </div>
                ) : null}
              </Command>
            </PopoverContent>
          </Popover>
          <span className="ml-auto whitespace-nowrap text-xs text-muted-foreground">
            {query.isFetching
              ? 'Loading…'
              : `${selectedCount} of ${query.data?.meta.total ?? 0} selected`}
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {/* `table-fixed` honors the explicit column widths below so the
              layout doesn't shift when the user filters or types in the
              search box and the row contents change length. */}
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    aria-label="Select all on page"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    className="size-4"
                  />
                </TableHead>
                <SortableHead
                  field="serviceRole"
                  label="Service role"
                  sort={sort}
                  onToggle={toggleSort}
                  className="w-44"
                />
                <SortableHead field="name" label="Name" sort={sort} onToggle={toggleSort} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`skel-${i}`}>
                    {Array.from({ length: 3 }).map((__, j) => (
                      <TableCell key={`skel-${i}-${j}`}>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="py-8 text-center text-sm text-muted-foreground">
                    No activities match.
                  </TableCell>
                </TableRow>
              ) : (
                products.map((p) => {
                  const isAdded = alreadyAddedIds?.has(p.id);
                  const isSelected = selected[p.id] != null;
                  const roleName = roleNameById.get(p.serviceRoleId);
                  return (
                    <TableRow
                      key={p.id}
                      className={cn(!isAdded && 'cursor-pointer', isAdded && 'opacity-50')}
                      onClick={() => {
                        if (!isAdded) toggle(p);
                      }}
                    >
                      <TableCell>
                        <input
                          type="checkbox"
                          aria-label={`Select ${p.name}`}
                          checked={isSelected}
                          disabled={isAdded}
                          onChange={() => toggle(p)}
                          onClick={(e) => e.stopPropagation()}
                          className="size-4"
                        />
                      </TableCell>
                      <TableCell className="truncate text-xs text-muted-foreground">
                        {roleName ??
                          (roleLabelsPending ? (
                            <Skeleton className="h-3 w-20" />
                          ) : (
                            // Only reachable for a role deleted out from
                            // under the activity — the by-id fetch resolves
                            // internal and deactivated roles.
                            `Role ${p.serviceRoleId.slice(0, 8)}…`
                          ))}
                      </TableCell>
                      <TableCell className="truncate">
                        {p.name}
                        {isAdded ? (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (already on engagement)
                          </span>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <SheetFooter className="flex-row justify-end border-t">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={selectedCount === 0}
            onClick={() => {
              onSubmit(Object.values(selected), roleShortCodeById);
              onOpenChange(false);
            }}
          >
            Add {selectedCount > 0 ? `${selectedCount} ` : ''}line{selectedCount === 1 ? '' : 's'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Header cell that toggles sort state on click. Shows an up/down arrow when
 * this column is the active sort, and a faint up-down icon otherwise so the
 * affordance is discoverable.
 */
function SortableHead({
  field,
  label,
  sort,
  onToggle,
  className,
}: {
  field: SortField;
  label: string;
  sort: { field: SortField; direction: SortDirection };
  onToggle: (field: SortField) => void;
  className?: string;
}) {
  const active = sort.field === field;
  const Icon = active ? (sort.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onToggle(field)}
        className="-mx-2 inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-muted"
      >
        {label}
        <Icon className={cn('size-3.5', active ? 'opacity-100' : 'opacity-40')} />
      </button>
    </TableHead>
  );
}
